import { Anchor, Button } from '../../../shared';
import {
  Content,
  Main,
  MainContent,
  Sidebar,
  Title,
  Top,
  TopRow,
  WarningIcon,
} from './CheckoutWrapper.styled';
import {
  ModalCloseButton,
  ModalCloseIcon,
  ModalContent,
  ModalWrapper,
} from '../../../shared/ConfirmationLoginModal/ConfirmationLoginModal.styled';
import { calculateDelivery, cartCheckout } from '../../../api/cart';
import { cleanCart, updateDeliveryCost } from '../../../store/cart/cartSlice';
import {
  faArrowLeft,
  faCartPlus,
  faExclamationCircle,
} from '@fortawesome/free-solid-svg-icons';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';

import { AuthRow } from '../../../components/Forms/Auth/AuthRow';
import { ButtonsWrapper } from '../../Account/AccountRow.styled';
import { CancelButton } from '../../../shared/DeleteConfirmationModal/DeleteConfirmationModal.styled';
import { FlowChekoutAndCartNotification } from '../../Notification';
import { OrderCost } from '../OrderCost';
import { OrderDeliveryType } from '../../../types/orders';
import { OrderProducts } from '../OrderProducts';
import { OrderType } from '../OrderType';
import { PaymentMethodsWrapper } from '../../PaymentMethods';
import { PromoCode } from '../PromoCode';
import { RegisterForm } from '../../../components/Forms/Auth/RegisterForm';
import { Step } from '../Step';
import { Tips } from '../Tips';
import { Wallet } from '../Wallet';
import { Wrapper } from '../../../components/ProductDetail/ProductDetail.styled';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { getTaxes } from '../../../utils/getTaxes';
import { getToken } from '../../../services/auth';
import { retreivePaymentMethods } from '../../../api/paymentMethods';
import { retreiveUserAddresses } from '../../../api/addresses';
import { setAddresses } from '../../../store/addresses/addressesSlice';
import { setPaymentMethods } from '../../../store/paymentMethods/paymentMethodsSlice';
import type { state } from '../../../types/store/state';
import { toast } from 'react-toastify';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { handleToastAsyncAction } from '../../../utils/handleToastAsyncAction';
import * as Sentry from '@sentry/react';

interface ParamsToSend {
  tip: string;
  paymentMethodId: number;
  orderType: OrderDeliveryType;
  useWallet: boolean;
  wallet: boolean;
  deliveryDatetime?: string;
}

export const CheckoutWrapper = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const router = useRouter();

  const { hasPickup, hasDelivery, isClosed } = useSelector(
    ({ branchStore }: state) => branchStore
  );
  const cart = useSelector(({ cart }: state) => cart);
  const primaryAddress = useSelector(
    ({ addresses }: state) =>
      addresses.results.filter((address) => address.primary),
    shallowEqual
  );
  const primaryPaymentMethod = useSelector(({ paymentMethods }: state) =>
    paymentMethods.results.filter((method) => method.primary)
  );

  let defaultOrderType: OrderDeliveryType =
    hasPickup && hasDelivery ? 'delivery' : 'pickup';

  if (hasPickup && !hasDelivery) {
    defaultOrderType = 'pickup';
  } else if (!hasPickup && hasDelivery) {
    defaultOrderType = 'delivery';
  }

  const [step, setStep] = useState<
    'type' | 'date' | 'payment' | 'listProducts'
  >('type');
  const [calculatedTip, setCalculatedTip] = useState<number>(0);
  const [confirmLoginModal, setConfirmLoginModal] = useState(false);
  const [locationsModal, setLocationsModal] = useState(false);
  const [orderRemotelyResponse, setOrderRemotelyResponse] = useState('');
  const [errServerResponse, setErrServerResponse] = useState('');
  const [useWallet, setUseWallet] = useState(false);
  const [walletAmmount, setWalletAmmount] = useState(0);
  const [orderType, setOrderType] =
    useState<OrderDeliveryType>(defaultOrderType);
  const [programmedTime, setProgrammedTime] = useState('');
  const [deliveryCostError, setDeliveryCostError] = useState(false);
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);

  const handleConfirmModal = () => {
    setConfirmLoginModal(!confirmLoginModal);
  };

  const handleConfirmLocationsModalClose = () => {
    setLocationsModal(false);
  };

  useEffect(() => {
    if (getToken() === 'guest-user') {
      setConfirmLoginModal(true);
    } else {
      retreivePaymentMethods()
        .then((res) => dispatch(setPaymentMethods(res)))
        .catch((error) => {
          throw new Error(error);
        });
      retreiveUserAddresses()
        .then((res) => dispatch(setAddresses(res)))
        .catch((error) => {
          throw new Error(error);
        });
    }
  }, []);

  useEffect(() => {
    if (primaryAddress.length && orderType === 'delivery' && cart.reference) {
      handleCheckDeliveryCost();
    } else {
      dispatch(updateDeliveryCost(0));
    }
  }, [orderType, primaryAddress, cart.reference]);

  const handleNextStep = () => {
    if (step === 'type') {
      setStep('listProducts');
    } else if (step === 'listProducts') {
      setStep('payment');
    }
  };

  const handleCheckDeliveryCost = async () => {
    const result = await handleToastAsyncAction(
      () => calculateDelivery({ id: cart.reference }),
      {
        showSuccessToast: false,
        displayToastType: 'warning',
        errorLogger: (error) => {
          setDeliveryCostError(true);
          dispatch(updateDeliveryCost(0));
          Sentry.captureException(error, {
            tags: {
              action: 'calculate_delivery_cost',
              cartReference: cart.reference
            }
          });
        }
      }
    );

    if (result.success && result.data) {
      setDeliveryCostError(false);
      dispatch(updateDeliveryCost(result.data.price));
    }
  };

  const handlePlaceOrder = async (orderRemotely: 0 | 1 = 0) => {

    if (isProcessingOrder) return;

    try {
      setIsProcessingOrder(true);

      if (!primaryPaymentMethod.length || (orderType === 'delivery' && hasDelivery && !primaryAddress.length)) {
        toast.error(t(
          !primaryPaymentMethod.length
            ? 'checkout.step.action.continue.toast.noPrimaryPayment'
            : 'checkout.step.action.continue.toast.noPrimaryAddress'
        ));
        setIsProcessingOrder(false);
        return;
      }

      const paramsToSend: ParamsToSend = {
        tip: calculatedTip.toFixed(2),
        paymentMethodId: primaryPaymentMethod[0].id,
        orderType,
        useWallet: useWallet,
        wallet: useWallet,
      };

      if (programmedTime) {
        try {
          const dateObject = new Date(programmedTime);
          if (isNaN(dateObject.getTime())) {
            throw new Error(t('ScheduleOrder.handlePlaceOrder.invalidDateFormat.error'));
          }
          paramsToSend.deliveryDatetime = dateObject.toISOString();
        } catch (error) {
          toast.error(t('ScheduleOrder.handlePlaceOrder.invalidDate.error'));
          console.error('Invalid date format:', error);
          setIsProcessingOrder(false);
          return;
        }
      }

      const result = await handleToastAsyncAction(
        () => cartCheckout({
          id: cart.reference,
          params: paramsToSend,
          query: orderRemotely,
        }),
        {
          onPendingMessage: t('checkout.action.processing'),
          showPendingToast: true,
          onSuccessMessage: t('cart.product.checkout.toastify.success'),
          showSuccessToast: true,
          showErrorToast: true,
          errorLogger: (error) => {
            console.error('Checkout order placement error:', error);
            Sentry.captureException(error, {
              tags: {
                action: 'checkout_order_placement',
                cartReference: cart.reference,
                orderType,
                orderRemotely: String(orderRemotely)
              }
            });
          },
          onErrorMessage: (error) => {
            const { data, status } = error.response || {};
            const errorKey = data && Object.keys(data)[0];

            if (orderRemotely === 0 && (data?.location || data?.address)) {
              const responseKey = data.location ? 'location' : 'address';
              setOrderRemotelyResponse(data[responseKey]);
              setLocationsModal(true);
              if (responseKey === 'address') {
                setErrServerResponse(data.address);
              }
              return null; // Suppress toast for this specific error on first attempt
            }

            return status <= 499 ? data?.[errorKey] : t('checkout.error.default');
          },
          displayToastType: 'error',
        }
      );

      if (result.success && result.data) {
        try {
          localStorage.setItem('just_checked_out', 'true');
        } catch (error) {
          Sentry.captureException(error, {
            tags: {
              action: 'set_just_checked_out_flag_failed',
              cartReference: cart.reference
            },
            extra: {
              message: 'Failed to set just_checked_out flag. Post-checkout new cart experience might be affected.',
            }
          });
        }
        await Promise.all([
          dispatch(cleanCart()),
          router.push(`/orders/${result.data.id}`)
        ]);
      }
    } catch (error) {
      console.error('Order processing error:', error);
    } finally {
      setIsProcessingOrder(false);
    }
  };

  const deliveryText = primaryAddress.length
    ? `${t('checkout.step.deliveryText.title')} ${primaryAddress[0].address.address
    }`
    : '';
  const paymentText = primaryPaymentMethod.length
    ? `${t('checkout.step.paymentText.title')} ${primaryPaymentMethod[0].brand
    } ${primaryPaymentMethod[0].last4}`
    : '';
  const subTitleTotalItems = `${t('checkout.step.orderProducts.subTitle')} ${cart.productsCount
    }`;

  const generateStepProps = () => ({
    title:
      hasDelivery && hasPickup
        ? t('checkout.step.deliverySelectOption.title')
        : hasDelivery
          ? t('checkout.step.deliveryDetails.title')
          : t('checkout.step.pickupDetails.title'),
    subtitle:
      hasDelivery && hasPickup
        ? t('checkout.step.deliveryAndPickupDetails.subTitle')
        : hasDelivery
          ? deliveryText
          : t('checkout.step.pickupDetails.subTitle'),
    open: hasDelivery && hasPickup ? true : step === 'type',
    nextStep: handleNextStep,
  });

  const stepProps = generateStepProps();

  return (
    <Main>
      {confirmLoginModal ? (
        <ModalWrapper>
          <ModalContent>
            <ModalCloseButton onClick={handleConfirmModal}>
              <ModalCloseIcon icon={faTimes} />
            </ModalCloseButton>
            <Wrapper>
              <AuthRow context="register" />
              <RegisterForm />
            </Wrapper>
          </ModalContent>
        </ModalWrapper>
      ) : null}
      <Top>
        <TopRow>
          <Title>{t('checkout.title')}</Title>
          {cart.productsCount < 1 ? null : (
            <Anchor
              title={t('cart.shared.action.toCart')}
              url="/cart/"
              icon={faArrowLeft}
              size="small"
              variation="secondary"
            />
          )}
        </TopRow>
      </Top>
      <Content>
        <MainContent>
          <Step
            orderType={orderType}
            title={stepProps.title}
            subtitle={stepProps.subtitle}
            open={stepProps.open}
            nextStep={stepProps.nextStep}
          >
            <OrderType
              setOrderType={setOrderType}
              programmedTime={programmedTime}
              setProgrammedTime={setProgrammedTime}
            />
          </Step>
          <Step
            orderType={orderType}
            title={
              <>
                {t('checkout.step.orderProducts.title')}
                {cart.productsCount < 1 && (
                  <WarningIcon icon={faExclamationCircle} />
                )}
              </>
            }
            subtitle={subTitleTotalItems}
            open={step === 'listProducts'}
            nextStep={cart.productsCount < 1 ? undefined : handleNextStep}
          >
            {cart.productsCount < 1 ? (
              <>{t('checkout.step.orderProducts.noItems')}</>
            ) : (
              <OrderProducts />
            )}
          </Step>
          <Step
            orderType={orderType}
            title={
              <>
                {t('checkout.step.paymentMethods.title')}
                {!primaryPaymentMethod.length && (
                  <WarningIcon icon={faExclamationCircle} />
                )}
              </>
            }
            subtitle={paymentText}
            open={step === 'payment'}
          >
            <PaymentMethodsWrapper condensed={true} />
          </Step>
        </MainContent>
        <Sidebar>
          <OrderCost
            orderCost={cart.orderCost}
            customTip={calculatedTip}
            customTax={
              cart.orderCost && cart.orderCost?.tax > 0
                ? cart.orderCost?.tax
                : getTaxes(cart.orderCost?.productsCost)
            }
            orderType={orderType}
            isLarge={false}
            useWallet={useWallet}
            walletAmmount={walletAmmount}
            customTotal
            deliveryCostError={deliveryCostError}
          />
          <Tips
            calculatedTip={calculatedTip}
            setCalculatedTip={setCalculatedTip}
            orderCost={cart.orderCost}
            orderType={orderType}
          />
          <Wallet
            useWallet={useWallet}
            setUseWallet={setUseWallet}
            walletAmmount={walletAmmount}
            setWalletAmmount={setWalletAmmount}
          />
          <PromoCode />
          <FlowChekoutAndCartNotification />
          <Anchor
            disabled={cart.productsCount > 0 ? isClosed || isProcessingOrder : true}
            title={
              isClosed
                ? t('checkout.action.storeIsClosed')
                : isProcessingOrder
                  ? t('checkout.action.processing')
                  : t('checkout.action.confirmOrder')
            }
            onClick={() => handlePlaceOrder()}
          />
          <Anchor
            title={t('cart.shared.action.keepBuying')}
            url="/"
            icon={faCartPlus}
            size="medium"
            variation="secondary"
          />
        </Sidebar>
        {locationsModal ? (
          <ModalWrapper>
            <ModalContent>
              <ModalCloseButton onClick={handleConfirmLocationsModalClose}>
                <ModalCloseIcon icon={faTimes} />
              </ModalCloseButton>
              <Wrapper>
                <div className="h3-locations-msg">
                  <h3>❗{orderRemotelyResponse}</h3>
                </div>
                <div className="h3-locations-msg">
                  <h3>{t('checkout.confirmOrder.locationsModal.continue')}</h3>
                </div>
                {orderRemotelyResponse === errServerResponse ? (
                  <div className="h4-locations-msg">
                    <h4>
                      {t(
                        'checkout.confirmOrder.locationsModal.farFromLocation.proceed'
                      )}
                    </h4>
                  </div>
                ) : (
                  <div className="h4-locations-msg">
                    <h4>
                      {t(
                        'checkout.confirmOrder.locationsModal.primaryAddressFarFromStore.proceed'
                      )}
                    </h4>
                  </div>
                )}
                <ButtonsWrapper>
                  <Button
                    title={t('checkout.modal.action.confirm')}
                    onClick={() => {
                      handlePlaceOrder(1);
                      handleConfirmLocationsModalClose();
                    }}
                  />
                  <CancelButton
                    title={t('checkout.modal.action.cancel')}
                    onClick={handleConfirmLocationsModalClose}
                  />
                </ButtonsWrapper>
                <br />
              </Wrapper>
            </ModalContent>
          </ModalWrapper>
        ) : null}
      </Content>
    </Main>
  );
};
